import React, { FC, useEffect, useState, useRef, useCallback, useMemo } from "react";
import CopyButton from "../uikit/CopyButton";
import { IAccount } from "@core/account/interface";
import * as dasha from "@dasha.ai/sdk/web";
import { Dropdown, Input, TextArea, Message, Label } from "semantic-ui-react";
import "./styles.css";
import { Loader } from "semantic-ui-react";
import { ActionButton } from "../uikit";
import WaveSurfer from "wavesurfer.js";
import { useStore } from "../core/api/GlobalStoreContext";
import { VoiceSelector } from "../widgets/VoiceSettings/VoiceSelector";
import { DEFAULT_PROVIDER_SETTINGS, ProviderVoiceSettings, transformProviderVoiceSettingsToDTO, VOICE_LANGUAGES, VOICE_PROVIDERS } from "../core/api/VoiceConfig";
import { FormSection } from "../widgets/WizardModal/components/FormElements";
import { StyledDropdown } from "../widgets/WizardModal/components/styles";
interface HistoryItem {
  text: string;
  speed: number;
  speaker: string;
  language: string;
  blob: Blob;
}

interface HistoryItemCombo {
  text: string;
  key: string;
  value: HistoryItem;
}

export const Tts: FC<{}> = ({}) => {
  const { account } = useStore();

  const [text, setText] = useState("Hi! How are you doing?");
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [wavesurfer, setWavesurfer] = useState<WaveSurfer | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [voice, setVoice] = useState(DEFAULT_PROVIDER_SETTINGS[VOICE_PROVIDERS.ELEVEN_LABS]);

  const [currentBlob, setCurrentBlob] = useState<Blob | undefined>(undefined);
  useEffect(() => {
    if (!containerRef.current) return;
    const ws = WaveSurfer.create({
      container: containerRef.current,
      plugins: [],
      height: 200,
      autoplay: true,
    });
    setWavesurfer(ws);

    return () => {
      ws.destroy();
    };
  }, [containerRef]);

  useEffect(() => {
    setCurrentBlob(undefined);
  }, [voice]);

  const onPlayClick = useCallback(() => {
    setLoading(true);
    setError(undefined);
    setCurrentBlob(undefined);
    if (wavesurfer === undefined) {
      return;
    }
    const fetchData = async () => {
      const a = account.connect();
      const options = { providerName: "dasha", account: a };
      const currentLanguage = VOICE_LANGUAGES[voice.provider] ? voice.language : "en-US";
      const voiceDTO = transformProviderVoiceSettingsToDTO({...voice, language: currentLanguage});
      const buffer = await dasha.tts.synthesize(text, voiceDTO, options);
      const blob = new Blob([buffer], { type: "audio/mp3" });
      setCurrentBlob(blob);
      wavesurfer?.loadBlob(blob);
    };

    fetchData()
      .catch((e) => setError(e.message))
      .then(() => setLoading(false));
  }, [wavesurfer, voice, account, text]);

  const onDownloadClick = useCallback(() => {
    if (!currentBlob) return;
    
    // Create filename with provider and voiceId
    const filename = `tts_${voice.provider}_${voice.providerVoiceId}.mp3`;
    
    // Create download URL and trigger download
    const url = URL.createObjectURL(currentBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [currentBlob, voice.provider, voice.providerVoiceId]);

  const getPhrasemap = useCallback(() => {
    return JSON.stringify(transformProviderVoiceSettingsToDTO(voice), null, 4);
  }, [voice]);
  return (
    <div className="defaultRoot" id="grid-workspace">
      <div>
        <h2>Text To Speech</h2>
        <VoiceSelector voice={voice} handleChange={(newVoice) => setVoice(newVoice)} />
          {VOICE_LANGUAGES[voice.provider] && (
              <FormSection>
              <Label>Language *</Label>
              <StyledDropdown
                selection
                fluid
                options={VOICE_LANGUAGES[voice.provider].map(l => ({
                  value: l.value,
                  text: l.label,
                  flag: l.flag,
                  key: l.value,
                }))}
                value={voice.language}
                onChange={(_, { value }) => {
                  setVoice({ ...voice, language: value as string });
                }}
                placeholder="Select speaker's native language..."
                error={!voice.language}
              />
            </FormSection>
          )}

        {error && <Message negative>Error {error}</Message>}
        <Loader active={loading}>Loading..</Loader>
        <CopyButton tooltipText="Copy phrasemap settings" hasIcon={true} clipboard={getPhrasemap()}>Copy settings</CopyButton>
        <h2>Text</h2>
        <TextArea
          value={text}
          placeholder="Your text"
          className="dasha-textarea"
          style={{ minHeight: 100, minWidth: 400 }}
          onChange={(e, { value }) => {
            setText(value?.toString() ?? "");
          }}
        />
      </div>
      
      <ActionButton onClick={onPlayClick}>[Synthesize]</ActionButton>
      <ActionButton onClick={onDownloadClick} disabled={!currentBlob}>Download</ActionButton>

      <div ref={containerRef} className="waveform" onClick={() => {
        if (wavesurfer?.isPlaying()) {
          wavesurfer?.pause();
        } else {
          wavesurfer?.play();
        }
      }} />
      

    </div>
  );
};
